import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, TableShell, Field, Drawer } from "@/components/ui-kit";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Repeat2, Calendar } from "lucide-react";

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MOBILITY = [
  { value: "ambulatory", label: "Ambulatory" },
  { value: "wheelchair", label: "Wheelchair" },
  { value: "stretcher", label: "Stretcher" },
];
const TRIP = [
  { value: "one_way", label: "One-way" },
  { value: "round_trip", label: "Round-trip" },
];

const empty = {
  company_id: "",
  customer_id: "",
  patient_name: "",
  pickup_address: "",
  dropoff_address: "",
  days_of_week: [],
  pickup_time: "08:00",
  start_date: new Date().toISOString().slice(0, 10),
  end_date: "",
  horizon_days: 90,
  mobility: "ambulatory",
  trip_type: "round_trip",
  payer: "",
  notes: "",
  active: true,
};
const fmtDeletedDate = (iso) => (iso ? String(iso).slice(0, 10) : "");

export default function RecurringRides() {
  const { user } = useAuth();
  const isAdmin = user?.role === "agency_admin";
  const [rows, setRows] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);

  const load = () => api.get(`/recurring${showDeleted ? "?include_deleted=true" : ""}`).then((r) => setRows(r.data));
  useEffect(() => {
    load();
    api.get("/companies").then((r) => setCompanies(r.data));
    api.get("/customers").then((r) => setCustomers(r.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDeleted]);

  const restore = async (id) => {
    try {
      await api.post(`/recurring/${id}/restore`);
      toast.success("Restored");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleDay = (idx) => {
    setForm((f) => {
      const set = new Set(f.days_of_week);
      set.has(idx) ? set.delete(idx) : set.add(idx);
      return { ...f, days_of_week: Array.from(set).sort() };
    });
  };

  const openNew = () => {
    setForm(empty);
    setEditingId(null);
    setOpen(true);
  };

  const openEdit = (r) => {
    setForm({
      ...empty,
      ...r,
      end_date: r.end_date || "",
      customer_id: r.customer_id || "",
      company_id: r.company_id || "",
    });
    setEditingId(r.id);
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        company_id: form.company_id || null,
        customer_id: form.customer_id || null,
        end_date: form.end_date || null,
        horizon_days: Number(form.horizon_days) || 90,
      };
      const { data } = editingId
        ? await api.put(`/recurring/${editingId}`, payload)
        : await api.post("/recurring", payload);
      toast.success(`Saved · ${data.scheduled_rides} rides scheduled`);
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    const n = row.scheduled_pending ?? 0;
    const msg = n === 0
      ? `Delete "${row.patient_name}" recurring pattern? This can be restored by an admin from Show deleted.\n\nNo future pending rides are tied to it, only the pattern itself will be removed.`
      : `Delete "${row.patient_name}" recurring pattern? This can be restored by an admin from Show deleted.\n\n⚠ This will also permanently drop ${n} future pending ride${n === 1 ? "" : "s"} that were auto-scheduled from it. Completed and in-progress rides are preserved.`;
    if (!confirm(msg)) return;
    await api.delete(`/recurring/${row.id}`);
    toast.success(n ? `Pattern and ${n} pending ride${n === 1 ? "" : "s"} deleted` : "Pattern deleted");
    load();
  };

  const companyName = (id) => companies.find((c) => c.id === id)?.name || <span className="text-slate-400">Direct</span>;
  const companyOpts = [{ value: "", label: "— None —" }, ...companies.map((c) => ({ value: c.id, label: c.name }))];
  const customerOpts = [{ value: "", label: "— None —" }, ...customers.map((c) => ({ value: c.id, label: `${c.first_name} ${c.last_name}` }))];

  const daySummary = (arr) => arr.map((d) => DAY_LABELS[d]).join(" · ");

  return (
    <div data-testid="recurring-page">
      <PageHeader
        subtitle="Standing trips"
        title="Recurring Rides"
        action={
          <div className="flex items-center gap-4">
            {isAdmin && (
              <label className="flex items-center gap-2 text-xs text-slate-600">
                <input type="checkbox" checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)} data-testid="recurring-show-deleted" />
                Show deleted
              </label>
            )}
            <button onClick={openNew} className="btn-primary flex items-center gap-1.5" data-testid="add-recurring-btn">
              <Repeat2 size={14} /> New pattern
            </button>
          </div>
        }
      />

      <div className="mb-4 text-sm text-slate-600 bg-white border border-slate-200 rounded-md p-4 flex items-start gap-3">
        <Calendar size={18} className="text-[#005E82] mt-0.5" strokeWidth={1.75} />
        <div>
          <div className="font-medium text-slate-800">How it works</div>
          Pick the days of the week and pickup time for a standing trip (dialysis, therapy, etc.). Rides are auto-scheduled as
          individual pending trips through the horizon you set. Edit a pattern to reshuffle future pending rides — completed
          trips are always preserved.
        </div>
      </div>

      <TableShell
        columns={["Patient", "Days", "Time", "Route", "Company", "Window", "Scheduled", "Actions"]}
        empty={rows.length === 0 ? "No recurring patterns yet. Create one for a standing dialysis or therapy trip." : null}
      >
        {rows.map((r) => {
          const isDeleted = !!r.deleted_at;
          return (
            <tr key={r.id} className={`tbl-row border-t border-slate-100 ${isDeleted ? "opacity-60" : ""}`} data-testid={`recurring-row-${r.id}`}>
              <td className="px-4 py-2.5 font-medium">
                {r.patient_name}
                <div className="text-slate-500 text-[11px] capitalize">{r.mobility} · {r.trip_type.replace("_", " ")}</div>
                {isDeleted && (
                  <span className="mt-1 inline-block bg-slate-100 text-slate-500 border border-slate-200 rounded-full px-2 py-0.5 text-[10px] normal-case">
                    deleted {fmtDeletedDate(r.deleted_at)}
                  </span>
                )}
              </td>
              <td className="px-4 py-2.5 text-slate-700 text-xs">{daySummary(r.days_of_week)}</td>
              <td className="px-4 py-2.5 font-mono text-xs">{r.pickup_time}</td>
              <td className="px-4 py-2.5 text-slate-600 max-w-[240px]">
                <div className="truncate">{r.pickup_address}</div>
                <div className="text-slate-400 truncate">→ {r.dropoff_address}</div>
              </td>
              <td className="px-4 py-2.5 text-slate-600">{companyName(r.company_id)}</td>
              <td className="px-4 py-2.5 font-mono text-xs">
                {r.start_date}
                <div className="text-slate-400">→ {r.end_date || `+${r.horizon_days}d`}</div>
              </td>
              <td className="px-4 py-2.5">
                <span className="inline-block bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5 text-xs font-mono">
                  {r.scheduled_pending ?? 0}
                </span>
              </td>
              <td className="px-4 py-2.5 space-x-3 whitespace-nowrap">
                {isDeleted ? (
                  <button className="text-emerald-700 hover:underline text-xs" onClick={() => restore(r.id)} data-testid={`restore-${r.id}`}>Restore</button>
                ) : (
                  <>
                    <button className="text-[#005E82] hover:underline text-xs" onClick={() => openEdit(r)} data-testid={`edit-recurring-${r.id}`}>Edit</button>
                    <button className="text-rose-600 hover:underline text-xs" onClick={() => remove(r)} data-testid={`delete-recurring-${r.id}`}>Delete</button>
                  </>
                )}
              </td>
            </tr>
          );
        })}
      </TableShell>

      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        title={editingId ? "Edit Pattern" : "New Recurring Pattern"}
        footer={
          <>
            <button onClick={() => setOpen(false)} className="text-sm px-3 py-2 rounded-md border border-slate-200">Cancel</button>
            <button onClick={save} disabled={saving} className="btn-primary" data-testid="save-recurring-btn">
              {saving ? "Saving…" : editingId ? "Save & re-schedule" : "Create & schedule"}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label="Patient name" testId="rr-patient" value={form.patient_name} onChange={(e) => setForm({ ...form, patient_name: e.target.value })} />
          <Field label="Link to customer (optional)" testId="rr-customer" value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })} options={customerOpts} />
          <Field label="Client company (optional)" testId="rr-company" value={form.company_id} onChange={(e) => setForm({ ...form, company_id: e.target.value })} options={companyOpts} />
          <Field label="Pickup address" testId="rr-pickup" value={form.pickup_address} onChange={(e) => setForm({ ...form, pickup_address: e.target.value })} />
          <Field label="Dropoff address" testId="rr-dropoff" value={form.dropoff_address} onChange={(e) => setForm({ ...form, dropoff_address: e.target.value })} />

          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500 block mb-2">Days of week</label>
            <div className="flex gap-1.5">
              {DAY_LABELS.map((d, i) => {
                const active = form.days_of_week.includes(i);
                return (
                  <button
                    key={d}
                    type="button"
                    onClick={() => toggleDay(i)}
                    className={`flex-1 py-2 rounded-md text-xs font-medium border transition ${active ? "bg-[#0A192F] text-white border-[#0A192F]" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}
                    data-testid={`day-${i}`}
                  >
                    {d}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Pickup time" testId="rr-time" type="time" value={form.pickup_time} onChange={(e) => setForm({ ...form, pickup_time: e.target.value })} />
            <Field label="Trip type" testId="rr-trip" value={form.trip_type} onChange={(e) => setForm({ ...form, trip_type: e.target.value })} options={TRIP} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Start date" testId="rr-start" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
            <Field label="End date (optional)" testId="rr-end" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Horizon (days)" testId="rr-horizon" type="number" value={form.horizon_days} onChange={(e) => setForm({ ...form, horizon_days: e.target.value })} />
            <Field label="Mobility" testId="rr-mobility" value={form.mobility} onChange={(e) => setForm({ ...form, mobility: e.target.value })} options={MOBILITY} />
          </div>

          <Field label="Payer" testId="rr-payer" value={form.payer} onChange={(e) => setForm({ ...form, payer: e.target.value })} placeholder="Medicaid / Insurance / Private" />
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3}
              className="mt-1 w-full text-sm px-3 py-2.5 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300"
            />
          </div>

          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="rr-active" />
            Active (unchecked pauses future scheduling)
          </label>
        </div>
      </Drawer>
    </div>
  );
}
